
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/api';
import { useUser } from '../context/UserContext';

const B = {
  black: '#1e1e23', surface: '#26262d', surfaceLight: '#2e2e36',
  border: '#1e1a2e', borderLight: '#2e2850',
  purple: '#7B5CC8', purpleDim: '#4a3880', purpleDeep: '#1a1030',
  gold: '#C9A84C', goldDim: '#8a6e2a',
  textPrimary: '#ffffff', textSecondary: '#8888aa', textMuted: '#444466',
  green: '#1D9E75', greenDeep: '#0a1f15', greenDim: '#0f6e56',
};

const RATINGS = {
  1: { label: 'Unsatisfactory', color: '#c0392b' },
  2: { label: 'Needs Improvement', color: '#C9A84C' },
  3: { label: 'Solid & Reliable', color: '#7B5CC8' },
  4: { label: 'Strong Performer', color: '#1D9E75' },
  5: { label: 'Outstanding', color: '#C9A84C' },
};

const PRIORITY_META = [
  { key: 'must_do', label: 'Must do', tag: '#c0392b' },
  { key: 'should_do', label: 'Should do', tag: '#C9A84C' },
  { key: 'could_do', label: 'Could do', tag: '#1D9E75' },
];

const GOAL_STATUSES = {
  done: { label: 'Done', color: '#1D9E75' },
  in_progress: { label: 'In Progress', color: '#C9A84C' },
  not_started: { label: 'Not Started', color: '#444466' },
  deprioritised: { label: 'Deprioritised', color: '#8888aa' },
};

const RatingTrend = ({ ratedCycles }) => {
  if (ratedCycles.length < 2) return null;
  const maxR = 5;
  const barW = 48;
  const gap = 40;
  const chartH = 80;

  return (
    <div style={{ background: B.surfaceLight, border: `0.5px solid ${B.border}`, borderRadius: 10, padding: '1.25rem 1.5rem', marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, textTransform: 'uppercase', margin: 0 }}>Rating trend</p>
        <p style={{ fontSize: 11, color: B.textMuted, margin: 0 }}>Annual reviews only</p>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap, justifyContent: 'center', height: chartH + 40 }}>
        {ratedCycles.map((item, i) => {
          const r = RATINGS[item.review.rating];
          const barH = Math.round((item.review.rating / maxR) * chartH);
          const isLatest = i === ratedCycles.length - 1;
          return (
            <div key={item.cycle.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: r.color, fontWeight: 500 }}>{item.review.rating}</span>
              <div style={{ width: barW, height: barH, background: isLatest ? B.purple : B.purpleDim, borderRadius: '4px 4px 0 0', border: `0.5px solid ${isLatest ? B.purple : B.purpleDim}` }} />
              <div style={{ width: barW, height: 1, background: B.border }} />
              <span style={{ fontSize: 11, color: isLatest ? B.textPrimary : B.textSecondary }}>{item.cycle.year}</span>
              <span style={{ fontSize: 10, color: r.color, textAlign: 'center', maxWidth: barW + 20 }}>{r.label}</span>
            </div>
          );
        })}
      </div>
      {(() => {
        const last = ratedCycles[ratedCycles.length - 1].review.rating;
        const prev = ratedCycles[ratedCycles.length - 2].review.rating;
        const diff = last - prev;
        if (diff === 0) return <p style={{ fontSize: 12, color: B.textSecondary, marginTop: 12, textAlign: 'center' }}>Rating unchanged from previous year.</p>;
        return <p style={{ fontSize: 12, color: diff > 0 ? B.green : '#c0392b', marginTop: 12, textAlign: 'center' }}>{diff > 0 ? '▲' : '▼'} {Math.abs(diff)} point{Math.abs(diff) > 1 ? 's' : ''} {diff > 0 ? 'up' : 'down'} from {ratedCycles[ratedCycles.length - 2].cycle.year}</p>;
      })()}
    </div>
  );
};

export default function EmployeeHistory() {
  const { employee } = useUser();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printMode, setPrintMode] = useState(false);

  useEffect(() => { fetchHistory(); }, [employee]);

  // Print mode: temporarily expand every cycle so the PDF captures
  // full content. Listen for the afterprint event to reset. (beforeprint
  // can't do the expansion synchronously — browser snapshots the DOM
  // before the paint — so we flip state, let React render, then call
  // print() from a later timer tick via setTimeout.)
  useEffect(() => {
    const restore = () => setPrintMode(false);
    window.addEventListener('afterprint', restore);
    return () => window.removeEventListener('afterprint', restore);
  }, []);

  const handlePrint = () => {
    setPrintMode(true);
    // Wait a tick so React commits the expanded state before the browser
    // takes its print snapshot.
    setTimeout(() => window.print(), 50);
  };

  const fetchHistory = async () => {
    if (!employee) return;
    setLoading(true);

    // Get all review cycles
    const { data: cycles } = await supabase.from('review_cycles').select('*').order('year', { ascending: false }).order('type', { ascending: true });

    // Get all annual reviews for this employee
    const { data: annualReviews } = await supabase.from('annual_reviews').select('*').eq('employee_id', employee.id);

    // Get all mid-year reviews for this employee
    const { data: midyearReviews } = await supabase.from('midyear_reviews').select('*').eq('employee_id', employee.id);

    // Merge
    const merged = (cycles || []).map(cycle => {
      const review = cycle.type === 'Annual'
        ? annualReviews?.find(r => r.cycle_id === cycle.id)
        : midyearReviews?.find(r => r.cycle_id === cycle.id);
      return { cycle, review: review || null };
    }).filter(item => item.review || item.cycle.is_open); // only show cycles with activity or open cycles

    setHistory(merged);

    // Auto-expand the most recently completed
    const firstCompleted = merged.find(item => item.review?.manager_status === 'completed' || item.review?.emp_status === 'submitted');
    if (firstCompleted) setExpanded(firstCompleted.cycle.id);

    setLoading(false);
  };

  const ratedCycles = history.filter(item => item.cycle.type === 'Annual' && item.review?.rating).sort((a, b) => a.cycle.year - b.cycle.year);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: B.black, color: B.textSecondary, fontSize: 12, letterSpacing: '0.08em' }}>LOADING...</div>;

  return (
    <div style={{ background: B.black, minHeight: '100vh', fontFamily: 'inherit' }}>
      {/* Header */}
      <div style={{ background: B.black, borderBottom: `0.5px solid ${B.border}`, padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>

            <img src="/logo-full.png" alt="xLight" style={{ height: 22, width: 'auto', display: 'block' }} />

          </button>
          <span style={{ width: 1, height: 16, background: B.border, margin: '0 8px' }} />
          <span style={{ fontSize: 11, color: B.textSecondary, letterSpacing: '0.08em', textTransform: 'uppercase' }}>My Reviews</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button
            onClick={handlePrint}
            className="no-print"
            style={{ fontSize: 11, letterSpacing: '0.06em', padding: '6px 14px', borderRadius: 6, border: `0.5px solid ${B.border}`, background: 'transparent', color: B.textSecondary, cursor: 'pointer' }}>
            ↓ SAVE AS PDF
          </button>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: 11, color: B.textSecondary, margin: 0, letterSpacing: '0.06em' }}>REVIEW HISTORY</p>
            <p style={{ fontSize: 12, color: B.textPrimary, margin: '2px 0 0' }}>{employee?.name} · {employee?.department}</p>
          </div>
        </div>
      </div>

      <div data-print-area style={{ maxWidth: 700, margin: '0 auto', padding: '1.5rem' }}>
        <RatingTrend ratedCycles={ratedCycles} />

        {history.length === 0 && (
          <p style={{ fontSize: 13, color: B.textMuted, textAlign: 'center', marginTop: '3rem' }}>No review history yet. Your first review will appear here once a cycle is open.</p>
        )}

        <p style={{ fontSize: 10, letterSpacing: '0.1em', color: B.textSecondary, marginBottom: 14, textTransform: 'uppercase' }}>All cycles</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {history.map(({ cycle, review }) => {
            const isOpen = printMode || expanded === cycle.id;
            const rObj = review?.rating ? RATINGS[review.rating] : null;
            const isCompleted = review?.manager_status === 'completed';
            const isInProgress = review?.emp_status === 'in_progress' || review?.emp_status === 'submitted';

            return (
              <div key={cycle.id} style={{ background: B.surfaceLight, border: `0.5px solid ${isOpen ? B.purpleDim : B.border}`, borderRadius: 10, overflow: 'hidden' }}>
                <button onClick={() => setExpanded(isOpen ? null : cycle.id)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: isCompleted ? B.purple : isInProgress ? B.gold : B.textMuted, flexShrink: 0 }} />
                    <div>
                      <p style={{ fontSize: 13, color: B.textPrimary, margin: 0 }}>{cycle.label}</p>
                      <p style={{ fontSize: 11, color: B.textSecondary, margin: '2px 0 0', letterSpacing: '0.03em' }}>{cycle.type} · {cycle.year} · {isCompleted ? 'Completed' : isInProgress ? 'In Progress' : cycle.is_open ? 'Open' : 'No submission'}</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {rObj && <span style={{ fontSize: 11, color: rObj.color }}>{rObj.label}</span>}
                    <span style={{ fontSize: 13, color: B.textMuted, display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>›</span>
                  </div>
                </button>

                {isOpen && (
                  <div style={{ padding: '0 16px 16px', borderTop: `0.5px solid ${B.border}` }}>
                    {!review && (
                      <div style={{ background: '#0f0d05', border: `0.5px solid ${B.goldDim}`, borderRadius: 8, padding: '10px 14px', margin: '14px 0' }}>
                        <p style={{ fontSize: 12, color: B.textSecondary, margin: 0 }}>{cycle.is_open ? 'This cycle is currently open. Your submission will appear here once you complete your review.' : 'No submission was recorded for this cycle.'}</p>
                      </div>
                    )}

                    {review && cycle.type === 'Annual' && (
                      <>
                        {/* Self eval */}
                        {(review.achievements || review.skills) && (
                          <div style={{ marginTop: 14 }}>
                            <p style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, marginBottom: 8, textTransform: 'uppercase' }}>Your self-evaluation</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {review.achievements && (
                                <div style={{ background: B.surface, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '10px 14px' }}>
                                  <p style={{ fontSize: 10, letterSpacing: '0.07em', color: B.textSecondary, marginBottom: 4, textTransform: 'uppercase' }}>Key achievements & learnings</p>
                                  <p style={{ fontSize: 13, color: B.textPrimary, lineHeight: 1.6, margin: 0 }}>{review.achievements}</p>
                                </div>
                              )}
                              {review.skills && (
                                <div style={{ background: B.surface, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '10px 14px' }}>
                                  <p style={{ fontSize: 10, letterSpacing: '0.07em', color: B.textSecondary, marginBottom: 4, textTransform: 'uppercase' }}>Skills & development</p>
                                  <p style={{ fontSize: 13, color: B.textPrimary, lineHeight: 1.6, margin: 0 }}>{review.skills}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Priorities */}
                        {(review.must_do || review.should_do || review.could_do) && (
                          <div style={{ marginTop: 14 }}>
                            <p style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, marginBottom: 8, textTransform: 'uppercase' }}>Priorities set</p>
                            <div style={{ display: 'flex', gap: 8 }}>
                              {PRIORITY_META.map(({ key, label, tag }) => (
                                <div key={key} style={{ flex: 1, background: B.surface, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '10px 10px' }}>
                                  <span style={{ fontSize: 9, padding: '1px 7px', borderRadius: 99, background: tag, color: '#fff', display: 'inline-block', marginBottom: 6 }}>{label.toUpperCase()}</span>
                                  <p style={{ fontSize: 12, color: review[key] ? B.textPrimary : B.textMuted, lineHeight: 1.5, margin: 0, fontStyle: review[key] ? 'normal' : 'italic' }}>{review[key] || '—'}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {review && cycle.type === 'Mid-Year' && (
                      <div style={{ marginTop: 14 }}>
                        <p style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, marginBottom: 8, textTransform: 'uppercase' }}>Priority progress</p>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                          {[{ key: 'must', label: 'Must do', tag: '#c0392b' }, { key: 'should', label: 'Should do', tag: '#C9A84C' }, { key: 'could', label: 'Could do', tag: '#1D9E75' }].map(({ key, label, tag }) => {
                            const statusKey = review[`${key}_status`];
                            const gs = statusKey ? GOAL_STATUSES[statusKey] : null;
                            return (
                              <div key={key} style={{ flex: 1, background: B.surface, border: `0.5px solid ${B.border}`, borderRadius: 8, padding: '10px 10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6, flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: 9, padding: '1px 7px', borderRadius: 99, background: tag, color: '#fff' }}>{label.toUpperCase()}</span>
                                  {gs && <span style={{ fontSize: 9, color: gs.color }}>{gs.label}</span>}
                                </div>
                                <p style={{ fontSize: 12, color: B.textPrimary, lineHeight: 1.5, margin: 0 }}>{review[`${key}_update`] || '—'}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Manager feedback */}
                    {(review?.manager_feedback || review?.manager_comments) && (
                      <div style={{ marginTop: 14 }}>
                        <p style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, marginBottom: 8, textTransform: 'uppercase' }}>Manager feedback</p>
                        <div style={{ background: B.purpleDeep, border: `0.5px solid ${B.purpleDim}`, borderRadius: 8, padding: '12px 14px', display: 'flex', gap: 12 }}>
                          <div style={{ width: 2, background: B.purple, borderRadius: 99, flexShrink: 0 }} />
                          <p style={{ fontSize: 13, color: B.textPrimary, lineHeight: 1.7, margin: 0 }}>{review.manager_feedback || review.manager_comments}</p>
                        </div>
                      </div>
                    )}

                    {/* Rating */}
                    {rObj && (
                      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <p style={{ fontSize: 10, letterSpacing: '0.08em', color: B.textSecondary, textTransform: 'uppercase', margin: 0 }}>Overall rating</p>
                        <span style={{ fontSize: 13, fontWeight: 500, color: rObj.color }}>{review.rating} — {rObj.label}</span>
                      </div>
                    )}

                    {/* Awaiting feedback notice */}
                    {review?.emp_status === 'submitted' && !isCompleted && (
                      <div style={{ marginTop: 14, background: '#0f0d05', border: `0.5px solid ${B.goldDim}`, borderRadius: 8, padding: '10px 14px' }}>
                        <p style={{ fontSize: 12, color: B.textSecondary, margin: 0 }}>Your manager has been notified and will complete their review shortly.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
